import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, router } from '@inertiajs/react';
import { Copyright } from 'lucide-react';
import { Card, Button } from '../../components/ui';
import Input, { InputLabel, InputError, InputGroup } from '../../components/ui/Input';
import ThemeToggle from '../../components/ui/ThemeToggle';
import Logo from '../../components/ui/Logo';
import { cn } from '../../lib/cn';

export default function Register({ errors, email: initialEmail, first_name, last_name, role: initialRole }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: initialEmail || '',
    password: '',
    password_confirmation: '',
    first_name: first_name || '',
    last_name: last_name || '',
    role: initialRole || 'teacher',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    router.post('/register', formData, {
      onFinish: () => setIsSubmitting(false),
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link href="/" className="inline-block no-underline">
            <Logo className="h-8" />
          </Link>
          <p className="mt-2 text-gray-600 dark:text-gray-400">{t('auth.createTeacherAccount')}</p>
        </div>

        <Card className="p-8">
          <form onSubmit={handleSubmit} data-testid="register-form" className="space-y-5">
            {errors && errors.length > 0 && (
              <div data-testid="register-errors" className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <ul className="list-disc list-inside text-sm text-red-600 dark:text-red-400 space-y-1">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <InputGroup>
                <InputLabel htmlFor="first_name">{t('auth.firstName')}</InputLabel>
                <Input
                  id="first_name"
                  data-testid="register-first-name"
                  type="text"
                  value={formData.first_name}
                  onChange={handleChange('first_name')}
                  placeholder={t('auth.firstNamePlaceholder')}
                  autoComplete="given-name"
                  autoFocus
                />
              </InputGroup>

              <InputGroup>
                <InputLabel htmlFor="last_name">{t('auth.lastName')}</InputLabel>
                <Input
                  id="last_name"
                  data-testid="register-last-name"
                  type="text"
                  value={formData.last_name}
                  onChange={handleChange('last_name')}
                  placeholder={t('auth.lastNamePlaceholder')}
                  autoComplete="family-name"
                />
              </InputGroup>
            </div>

            <InputGroup>
              <InputLabel htmlFor="email" required>{t('auth.email')}</InputLabel>
              <Input
                id="email"
                data-testid="register-email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                placeholder={t('auth.emailPlaceholder')}
                autoComplete="email"
                required
              />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="password" required>{t('auth.password')}</InputLabel>
              <Input
                id="password"
                data-testid="register-password"
                type="password"
                value={formData.password}
                onChange={handleChange('password')}
                placeholder={t('auth.passwordHint')}
                autoComplete="new-password"
                required
              />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="password_confirmation" required>{t('auth.confirmPassword')}</InputLabel>
              <Input
                id="password_confirmation"
                data-testid="register-password-confirmation"
                type="password"
                value={formData.password_confirmation}
                onChange={handleChange('password_confirmation')}
                placeholder={t('auth.confirmPasswordPlaceholder')}
                autoComplete="new-password"
                required
              />
            </InputGroup>

            <input type="hidden" name="role" value={formData.role} />

            <Button
              type="submit"
              data-testid="register-submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('auth.creatingAccount') : t('auth.createAccount')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link
                href="/login"
                className="font-medium text-gray-900 dark:text-white hover:underline"
              >
                {t('auth.signInHere')}
              </Link>
            </p>
          </div>
        </Card>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('auth.areYouStudent')}{' '}
            <Link
              href="/join"
              className="font-medium text-gray-700 dark:text-gray-300 hover:underline"
            >
              {t('auth.joinWithCode')}
            </Link>
          </p>
        </div>

        <footer className="mt-8 text-center space-y-2">
          <div className="flex items-center justify-center gap-4 text-xs text-gray-400 dark:text-gray-500">
            <Link href="/privacy" className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors no-underline">Privacy</Link>
            <span>·</span>
            <Link href="/terms" className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors no-underline">Terms</Link>
          </div>
          <p className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
            <Copyright className="w-3.5 h-3.5" />
            {t('common.studyfitInc')}
          </p>
        </footer>
      </div>
    </div>
  );
}
